import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../utils/cn';

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | undefined>(undefined);

interface DropdownMenuProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const triggerRef = useRef<HTMLElement | null>(null);
  
  const setOpen = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef }}>
      {children}
    </DropdownMenuContext.Provider>
  );
};

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

type TriggerChildProps = {
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  ref?: React.Ref<any>;
  [key: string]: any;
};

export const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({
  children,
  asChild = false,
  className,
}) => {
  const context = React.useContext(DropdownMenuContext);
  if (!context) {
    throw new Error('DropdownMenuTrigger must be used within DropdownMenu');
  }

  const { open, setOpen, triggerRef } = context;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  if (asChild && React.isValidElement(children)) {
    const childProps = children.props as TriggerChildProps;
    return React.cloneElement(children, {
      ...childProps,
      onClick: (e: React.MouseEvent) => {
        handleClick(e);
        childProps.onClick?.(e);
      },
      ref: (node: HTMLElement | null) => {
        triggerRef.current = node;
        // Call original ref if it exists
        if (typeof childProps.ref === 'function') {
          childProps.ref(node);
        } else if (childProps.ref) {
          (childProps.ref as React.MutableRefObject<HTMLElement | null>).current = node;
        }
      },
      className: cn(className, childProps.className),
      'data-dropdown-trigger': true,
    } as any);
  }

  return (
    <div
      ref={triggerRef as React.RefObject<HTMLDivElement>}
      onClick={handleClick}
      className={cn('inline-flex', className)}
      data-dropdown-trigger
    >
      {children}
    </div>
  );
};

interface DropdownMenuContentProps {
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
  className?: string;
}

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
  children,
  align = 'end',
  side = 'bottom',
  className,
}) => {
  const context = React.useContext(DropdownMenuContext);
  if (!context) {
    throw new Error('DropdownMenuContent must be used within DropdownMenu');
  }

  const { open, setOpen, triggerRef } = context;
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Calculate and update position
  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !contentRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    
    // If content hasn't rendered yet, use estimated dimensions
    const contentWidth = contentRect.width || 200;
    const contentHeight = contentRect.height || 100;
    
    let top = 0;
    let left = 0;
    
    if (side === 'bottom') {
      top = triggerRect.bottom + 8; // mt-2 = 8px
    } else {
      top = triggerRect.top - contentHeight - 8; // mb-2 = 8px
    }
    
    if (align === 'end') {
      left = triggerRect.right - contentWidth;
    } else if (align === 'center') {
      left = triggerRect.left + (triggerRect.width / 2) - (contentWidth / 2);
    } else {
      left = triggerRect.left;
    }
    
    // Ensure menu stays within viewport
    const padding = 8;
    if (left < padding) left = padding;
    if (left + contentWidth > window.innerWidth - padding) {
      left = window.innerWidth - contentWidth - padding;
    }
    if (top < padding) top = padding;
    if (top + contentHeight > window.innerHeight - padding) {
      top = window.innerHeight - contentHeight - padding;
    }
    
    setPosition({ top, left });
  }, [align, side, triggerRef]);

  // Update position when content is rendered or window changes
  useEffect(() => {
    if (!open) return;

    // Initial position calculation after render - use requestAnimationFrame for better timing
    const rafId = requestAnimationFrame(() => {
      updatePosition();
      // Also update after a short delay to ensure content is fully rendered
      setTimeout(updatePosition, 10);
    });

    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // Update whenever the popover content changes size (e.g. crédito row appearing)
    let resizeObserver: ResizeObserver | null = null;
    if (contentRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      resizeObserver?.disconnect();
    };
  }, [open, updatePosition]);

  // Handle outside clicks
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentRef.current &&
        triggerRef.current &&
        !contentRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen, triggerRef]);

  // Handle Escape key
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, setOpen]);

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={contentRef}
          initial={{ opacity: 0, scale: 0.95, y: side === 'top' ? 10 : -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: side === 'top' ? 10 : -10 }}
          transition={{ duration: 0.2 }}
          className={cn(
            'fixed z-[9999] min-w-[200px] bg-background border border-border rounded-lg shadow-lg',
            className
          )}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Render using portal to body to escape stacking context
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
};

interface DropdownMenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  children,
  onClick,
  disabled = false,
  className,
}) => {
  const context = React.useContext(DropdownMenuContext);
  if (!context) {
    throw new Error('DropdownMenuItem must be used within DropdownMenu');
  }

  const { setOpen } = context;

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
      setOpen(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'w-full px-4 py-2 text-left hover:bg-muted transition-colors font-sans font-normal text-sm text-text flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
    >
      {children}
    </button>
  );
};

export default DropdownMenu;

